import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Derives a human-readable page name from the pathname.
 * e.g. "/dashboard" -> "Dashboard", "/my-recordings" -> "My Recordings"
 */
function getPageName(pathname) {
  const names = {
    '/': 'Dashboard',
    '/dashboard': 'Dashboard',
    '/teams': 'Care Teams',
    '/my-recordings': 'My Recordings',
    '/progress': 'Your Progress',
    '/profile': 'My Profile',
    '/account-settings': 'Account Settings',
    '/notifications': 'Notifications',
    '/login': 'Sign In',
    '/register': 'Sign Up',
    '/verify-email': 'Verify Email',
    '/verification-pending': 'Verification Pending',
    '/forgot-password': 'Forgot Password',
    '/reset-password': 'Reset Password',
    '/restore-account': 'Restore Account',
    '/privacy': 'Privacy Policy',
    '/terms': 'Terms of Service',
    '/under-construction': 'Under Construction',
  };

  // Exact match
  if (names[pathname]) return names[pathname];

  // Prefix match for dynamic routes (e.g. /team/:teamId, /questionnaire/:id)
  if (pathname.startsWith('/team/')) return 'Care Team';
  if (pathname.startsWith('/questionnaire')) return 'Questionnaire';

  // Fallback: convert pathname to title case
  const segment = pathname.split('/').filter(Boolean).pop() || '';
  return segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ') || 'Page';
}

/**
 * Component that announces route changes to screen readers.
 * Renders an aria-live region that updates when the location changes.
 */
export default function RouteAnnouncer() {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState('');
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Don't announce on initial page load
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const pageName = getPageName(location.pathname);
    setAnnouncement(`Navigated to ${pageName}`);
  }, [location.pathname]);

  return (
    <div
      role="status"
      aria-live="assertive"
      aria-atomic="true"
      className="sr-only"
    >
      {announcement}
    </div>
  );
}
